/**
 * MediaDropzone.tsx
 *
 * Content-Addressed Media Asset Dropzone:
 * - Drag-and-drop file ingestion (JPEG, PNG, WebP, SVG, PDF).
 * - Client-side pre-flight canvas downsampler (max 2048x2048, WebP q=0.85) to conserve bandwidth.
 * - Multi-locale alt-text editor (en, es, zh-Hans, ar) with accessibility & decorative support.
 * - Authoritative trusted ingestion via globalMediaAssetService with SHA-256 deduplication.
 * - Primary badge assignment and thumbnail gallery preview.
 */

import React, { useState, useRef } from 'react';
import {
  globalMediaAssetService,
  type IngestMediaOptions,
} from '../../plugins/commerce/media/mediaAssetService.js';
import type {
  MediaAsset,
  MediaOwnerType,
  MediaMimeType,
} from '../../plugins/commerce/inventory/inventoryContracts.js';
import type { AuthorizationContext } from '../../kernel/authorization/capabilities.js';

export interface MediaDropzoneProps {
  readonly ownerType: MediaOwnerType;
  readonly ownerId: string;
  readonly existingMedia: readonly MediaAsset[];
  readonly authContext?: AuthorizationContext;
  readonly onMediaAttached: (media: MediaAsset) => void;
  readonly onMediaRemoved: (mediaId: string) => void;
  readonly onSetPrimary?: (mediaId: string) => void;
  readonly disabled?: boolean;
}

const SUPPORTED_LOCALES = [
  { code: 'en', label: 'English (US)' },
  { code: 'es', label: 'Español' },
  { code: 'zh-Hans', label: '中文 (简体)' },
  { code: 'ar', label: 'العربية' },
];

/**
 * Pre-flight canvas downsampler for browser memory & bandwidth efficiency.
 */
async function preflightDownsample(file: File): Promise<{ bytes: Uint8Array; mimeType: MediaMimeType }> {
  // SVG and PDF skip raster canvas downsampling
  if (file.type === 'image/svg+xml' || file.name.endsWith('.svg')) {
    const text = await file.text();
    return { bytes: new TextEncoder().encode(text), mimeType: 'image/svg+xml' };
  }
  if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
    const buf = await file.arrayBuffer();
    return { bytes: new Uint8Array(buf), mimeType: 'application/pdf' };
  }

  // Attempt HTML5 Canvas downsampling for raster images
  if (typeof document !== 'undefined' && typeof Image !== 'undefined') {
    try {
      const bitmap = await createImageBitmap(file);
      const MAX_DIM = 2048;
      let width = bitmap.width;
      let height = bitmap.height;

      if (width > MAX_DIM || height > MAX_DIM) {
        if (width > height) {
          height = Math.round((height * MAX_DIM) / width);
          width = MAX_DIM;
        } else {
          width = Math.round((width * MAX_DIM) / height);
          height = MAX_DIM;
        }
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(bitmap, 0, 0, width, height);
        const blob = await new Promise<Blob | null>((resolve) =>
          canvas.toBlob(resolve, 'image/webp', 0.85)
        );
        if (blob) {
          const buf = await blob.arrayBuffer();
          return { bytes: new Uint8Array(buf), mimeType: 'image/webp' };
        }
      }
    } catch {
      // Fallback to raw bytes if createImageBitmap fails
    }
  }

  const buf = await file.arrayBuffer();
  const rawMime = file.type as MediaMimeType;
  return { bytes: new Uint8Array(buf), mimeType: rawMime || 'image/jpeg' };
}

export interface MediaProcessState {
  readonly fileId: string;
  readonly fileName: string;
  readonly stage: 'queued' | 'decoding' | 'resizing' | 'hashing' | 'uploading' | 'stored' | 'failed' | 'cancelled';
  readonly progress: number;
  readonly message: string;
}

export const MediaDropzone: React.FC<MediaDropzoneProps> = ({
  ownerType,
  ownerId,
  existingMedia,
  authContext,
  onMediaAttached,
  onMediaRemoved,
  onSetPrimary,
  disabled = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [ingestMode, setIngestMode] = useState<'upload' | 'url'>('upload');
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [copiedMediaId, setCopiedMediaId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [activeProcess, setActiveProcess] = useState<MediaProcessState | null>(null);
  const [selectedLocale, setSelectedLocale] = useState('en');
  const [altTextMap, setAltTextMap] = useState<Record<string, string>>({});
  const [isDecorative, setIsDecorative] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isCancelledRef = useRef(false);

  const handleUrlIngest = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!imageUrlInput.trim() || disabled) return;
    isCancelledRef.current = false;
    setErrorMessage(null);

    const targetUrl = imageUrlInput.trim();
    try {
      setActiveProcess({
        fileId: 'proc-url',
        fileName: targetUrl.slice(0, 35) + '...',
        stage: 'uploading',
        progress: 35,
        message: `Fetching & generating UUIDv7 reference for remote media...`,
      });

      const options: IngestMediaOptions = {
        ownerType,
        ownerId,
        role: existingMedia.length === 0 ? 'primary' : 'gallery',
        visibility: ownerType === 'certificate' || ownerType === 'batch' ? 'admins-only' : 'public-catalog',
        altText: isDecorative ? {} : altTextMap,
        altTextStatus: isDecorative ? 'provided' : Object.keys(altTextMap).length > 0 ? 'provided' : 'missing',
        sortOrder: existingMedia.length,
      };

      const result = await globalMediaAssetService.ingestFromUrl(targetUrl, options);
      onMediaAttached(result.media);

      setActiveProcess({
        fileId: 'proc-url',
        fileName: result.media.mediaId,
        stage: 'stored',
        progress: 100,
        message: `Successfully ingested remote asset (${result.media.mediaId})`,
      });

      setImageUrlInput('');
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to ingest media from URL');
    } finally {
      setTimeout(() => setActiveProcess(null), 1200);
    }
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0 || disabled) return;
    isCancelledRef.current = false;
    setErrorMessage(null);

    try {
      const totalFiles = files.length;
      for (let i = 0; i < totalFiles; i++) {
        if (isCancelledRef.current) break;

        const file = files[i];
        if (file.size > 10 * 1024 * 1024) {
          throw new Error(`File "${file.name}" exceeds 10MB limit`);
        }

        // 1. Stage: Decoding & Resizing
        setActiveProcess({
          fileId: `proc-${i}`,
          fileName: file.name,
          stage: 'resizing',
          progress: Math.round(((i * 4 + 1) / (totalFiles * 4)) * 100),
          message: `Downsampling & optimizing image ${i + 1} of ${totalFiles} (${file.name})...`,
        });

        const { bytes, mimeType } = await preflightDownsample(file);
        if (isCancelledRef.current) break;

        // 2. Stage: Hashing & Magic Byte Validation
        setActiveProcess({
          fileId: `proc-${i}`,
          fileName: file.name,
          stage: 'hashing',
          progress: Math.round(((i * 4 + 2) / (totalFiles * 4)) * 100),
          message: `Verifying MIME magic-bytes and computing SHA-256 hash...`,
        });

        const options: IngestMediaOptions = {
          ownerType,
          ownerId,
          declaredMimeType: mimeType,
          role: existingMedia.length === 0 && i === 0 ? 'primary' : 'gallery',
          visibility: ownerType === 'certificate' || ownerType === 'batch' ? 'admins-only' : 'public-catalog',
          altText: isDecorative ? {} : altTextMap,
          altTextStatus: isDecorative ? 'provided' : Object.keys(altTextMap).length > 0 ? 'provided' : 'missing',
          sortOrder: existingMedia.length + i,
        };

        // 3. Stage: Authoritative Ingestion
        setActiveProcess({
          fileId: `proc-${i}`,
          fileName: file.name,
          stage: 'uploading',
          progress: Math.round(((i * 4 + 3) / (totalFiles * 4)) * 100),
          message: `Registering content-addressed storage ref (UUIDv7)...`,
        });

        const result = await globalMediaAssetService.ingestMedia(bytes, mimeType, options);
        onMediaAttached(result.media);

        // 4. Stage: Stored
        setActiveProcess({
          fileId: `proc-${i}`,
          fileName: file.name,
          stage: 'stored',
          progress: Math.round(((i + 1) / totalFiles) * 100),
          message: `Successfully ingested ${file.name} (${result.media.mediaId})`,
        });
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to ingest media file');
    } finally {
      setTimeout(() => setActiveProcess(null), 1200);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleAltChange = (locale: string, text: string) => {
    setAltTextMap((prev) => ({
      ...prev,
      [locale]: text,
    }));
  };

  const copyMediaReference = (mediaId: string) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(mediaId);
      setCopiedMediaId(mediaId);
      setTimeout(() => setCopiedMediaId(null), 2000);
    }
  };

  const isProcessing = !!activeProcess && activeProcess.stage !== 'stored';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {/* Ingestion Mode Switcher */}
      <div style={{ display: 'flex', gap: '6px', borderBottom: '1px solid var(--theme-border, #1e293b)', paddingBottom: '8px' }}>
        <button
          type="button"
          onClick={() => setIngestMode('upload')}
          style={{
            padding: '6px 14px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: 700,
            cursor: 'pointer',
            border: 'none',
            backgroundColor: ingestMode === 'upload' ? 'var(--theme-accent, #38bdf8)' : 'rgba(255, 255, 255, 0.05)',
            color: ingestMode === 'upload' ? '#000' : 'var(--theme-text-secondary, #94a3b8)',
          }}
        >
          📁 Upload Files
        </button>
        <button
          type="button"
          onClick={() => setIngestMode('url')}
          style={{
            padding: '6px 14px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: 700,
            cursor: 'pointer',
            border: 'none',
            backgroundColor: ingestMode === 'url' ? 'var(--theme-accent, #38bdf8)' : 'rgba(255, 255, 255, 0.05)',
            color: ingestMode === 'url' ? '#000' : 'var(--theme-text-secondary, #94a3b8)',
          }}
        >
          🌐 Load from URL
        </button>
      </div>

      {/* Mode 1: Drop Target Area */}
      {ingestMode === 'upload' ? (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled) setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => !disabled && fileInputRef.current?.click()}
          style={{
            border: isDragging ? '2px dashed #38bdf8' : '2px dashed var(--theme-border, #334155)',
            borderRadius: '10px',
            padding: '20px 16px',
            textAlign: 'center',
            backgroundColor: isDragging ? 'rgba(56, 189, 248, 0.08)' : 'rgba(0, 0, 0, 0.2)',
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'all 0.15s ease-in-out',
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp,image/svg+xml,application/pdf"
            style={{ display: 'none' }}
            onChange={(e) => handleFiles(e.target.files)}
            disabled={disabled}
          />
          <div style={{ fontSize: '26px', marginBottom: '6px' }}>
            {isProcessing ? '⏳' : '📸'}
          </div>
          <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--theme-text-primary, #f8fafc)' }}>
            {isProcessing ? activeProcess?.message : 'Click or Drag & Drop Media Assets'}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--theme-text-secondary, #94a3b8)', marginTop: '4px' }}>
            Supports WebP, JPEG, PNG, SVG (Sanitized), and PDF certificates up to 10MB (Stored with UUIDv7 reference).
          </div>
        </div>
      ) : (
        /* Mode 2: Load from URL Form */
        <form onSubmit={handleUrlIngest} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="url"
              placeholder="https://images.unsplash.com/... or https://cdn.example.com/item.jpg"
              value={imageUrlInput}
              onChange={(e) => setImageUrlInput(e.target.value)}
              disabled={disabled || isProcessing}
              style={{
                flex: 1,
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid var(--theme-border, #334155)',
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                color: '#fff',
                fontSize: '13px',
                outline: 'none',
              }}
            />
            <button
              type="submit"
              disabled={disabled || !imageUrlInput.trim() || isProcessing}
              style={{
                padding: '10px 18px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: 'var(--theme-accent, #38bdf8)',
                color: '#000',
                fontWeight: 700,
                fontSize: '12px',
                cursor: disabled || !imageUrlInput.trim() || isProcessing ? 'not-allowed' : 'pointer',
              }}
            >
              📥 Ingest URL
            </button>
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center', fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)' }}>
            <span>Presets:</span>
            {[
              { label: 'Tactical Camera', url: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&auto=format&fit=crop&q=80' },
              { label: 'Armored Vault', url: 'https://images.unsplash.com/photo-1582139329536-e7284fece509?w=800&auto=format&fit=crop&q=80' },
              { label: 'Command SOC', url: 'https://images.unsplash.com/photo-1508873696983-2df57046475a?w=800&auto=format&fit=crop&q=80' },
            ].map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={() => setImageUrlInput(p.url)}
                style={{
                  background: 'none',
                  border: '1px solid var(--theme-border, #334155)',
                  borderRadius: '4px',
                  color: 'var(--theme-accent, #38bdf8)',
                  fontSize: '10px',
                  padding: '2px 6px',
                  cursor: 'pointer',
                }}
              >
                + {p.label}
              </button>
            ))}
          </div>
        </form>
      )}

      {/* Real-Time Animated Progress Bar */}
      {isProcessing && activeProcess && (
        <div
          style={{
            padding: '10px 14px',
            borderRadius: '8px',
            backgroundColor: 'rgba(56, 189, 248, 0.08)',
            border: '1px solid rgba(56, 189, 248, 0.3)',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px' }}>
            <span style={{ fontWeight: 700, color: '#38bdf8' }}>
              ⚡ {activeProcess.message}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontWeight: 800, color: '#fff' }}>{activeProcess.progress}%</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  isCancelledRef.current = true;
                  setActiveProcess(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#f87171',
                  fontSize: '10px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  padding: '2px 4px',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
          <div style={{ width: '100%', height: '6px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '3px', overflow: 'hidden' }}>
            <div
              style={{
                width: `${activeProcess.progress}%`,
                height: '100%',
                backgroundColor: '#38bdf8',
                borderRadius: '3px',
                transition: 'width 0.2s ease-in-out',
                boxShadow: '0 0 8px #38bdf8',
              }}
            />
          </div>
        </div>
      )}

      {/* Error Message */}
      {errorMessage && (
        <div
          style={{
            padding: '8px 12px',
            borderRadius: '6px',
            backgroundColor: 'rgba(239, 68, 68, 0.15)',
            border: '1px solid #ef4444',
            color: '#fca5a5',
            fontSize: '12px',
          }}
        >
          ⚠️ {errorMessage}
        </div>
      )}

      {/* Accessibility & Multi-Locale Alt-Text Configurator */}
      <div
        style={{
          padding: '12px',
          borderRadius: '8px',
          backgroundColor: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid var(--theme-border, #1e293b)',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--theme-text-primary, #f8fafc)' }}>
            🌐 Accessible Alt-Text (WCAG 2.1 AA)
          </span>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={isDecorative}
              onChange={(e) => setIsDecorative(e.target.checked)}
            />
            <span>Decorative Only (Empty Alt)</span>
          </label>
        </div>

        {!isDecorative && (
          <>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {SUPPORTED_LOCALES.map((loc) => (
                <button
                  key={loc.code}
                  type="button"
                  onClick={() => setSelectedLocale(loc.code)}
                  style={{
                    padding: '4px 10px',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    backgroundColor: selectedLocale === loc.code ? '#38bdf8' : 'rgba(255, 255, 255, 0.05)',
                    color: selectedLocale === loc.code ? '#000' : 'var(--theme-text-secondary, #94a3b8)',
                    border: '1px solid var(--theme-border, #334155)',
                  }}
                >
                  {loc.label} {altTextMap[loc.code] ? '✓' : ''}
                </button>
              ))}
            </div>

            <input
              type="text"
              placeholder={`Enter concise descriptive alt-text for [${selectedLocale.toUpperCase()}]...`}
              value={altTextMap[selectedLocale] || ''}
              onChange={(e) => handleAltChange(selectedLocale, e.target.value)}
              style={{
                width: '100%',
                padding: '8px 10px',
                borderRadius: '6px',
                border: '1px solid var(--theme-border, #334155)',
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                color: '#fff',
                fontSize: '12px',
                outline: 'none',
              }}
            />
          </>
        )}
      </div>

      {/* Media Gallery & Primary Assignment */}
      {existingMedia.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--theme-text-muted, #94a3b8)', textTransform: 'uppercase' }}>
            Attached Media ({existingMedia.length})
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '10px' }}>
            {existingMedia.map((m) => {
              const displayUrl = globalMediaAssetService.resolveDisplayUrl(m.mediaId, authContext);
              const isPrimary = m.role === 'primary';
              const isCopied = copiedMediaId === m.mediaId;

              return (
                <div
                  key={m.mediaId}
                  style={{
                    position: 'relative',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: isPrimary ? '2px solid #38bdf8' : '1px solid var(--theme-border, #334155)',
                    backgroundColor: '#000',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <div style={{ position: 'relative', width: '100%', height: '90px' }}>
                    <img
                      src={displayUrl}
                      alt={m.altText?.en || m.altText?.es || 'Product asset'}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />

                    {/* Primary Badge */}
                    {isPrimary ? (
                      <div
                        style={{
                          position: 'absolute',
                          top: '4px',
                          left: '4px',
                          backgroundColor: '#38bdf8',
                          color: '#000',
                          fontSize: '9px',
                          fontWeight: 800,
                          padding: '1px 5px',
                          borderRadius: '4px',
                        }}
                      >
                        PRIMARY
                      </div>
                    ) : (
                      onSetPrimary && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSetPrimary(m.mediaId);
                          }}
                          style={{
                            position: 'absolute',
                            top: '4px',
                            left: '4px',
                            backgroundColor: 'rgba(0,0,0,0.6)',
                            color: '#cbd5e1',
                            fontSize: '9px',
                            padding: '2px 4px',
                            borderRadius: '4px',
                            border: 'none',
                            cursor: 'pointer',
                          }}
                        >
                          Set Primary
                        </button>
                      )
                    )}

                    {/* Delete Button */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onMediaRemoved(m.mediaId);
                      }}
                      style={{
                        position: 'absolute',
                        top: '4px',
                        right: '4px',
                        backgroundColor: 'rgba(239, 68, 68, 0.8)',
                        color: '#fff',
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        border: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '11px',
                        cursor: 'pointer',
                      }}
                    >
                      ×
                    </button>
                  </div>

                  {/* UUIDv7 Reference Badge & Copy Action */}
                  <div style={{ padding: '6px', backgroundColor: 'rgba(15, 23, 42, 0.95)', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '9px', fontFamily: 'monospace', color: 'var(--theme-accent, #38bdf8)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '85px' }} title={m.mediaId}>
                        {m.mediaId.length > 14 ? `${m.mediaId.slice(0, 14)}...` : m.mediaId}
                      </span>
                      <button
                        type="button"
                        onClick={() => copyMediaReference(m.mediaId)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: isCopied ? '#34d399' : 'var(--theme-text-muted, #94a3b8)',
                          fontSize: '9px',
                          cursor: 'pointer',
                          padding: '1px 3px',
                          fontWeight: 700,
                        }}
                        title="Copy RFC-9562 UUIDv7 Media Reference"
                      >
                        {isCopied ? '✓' : '📋'}
                      </button>
                    </div>
                    {m.publicUrl && (
                      <span style={{ fontSize: '8px', color: 'var(--theme-text-muted, #64748b)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={m.publicUrl}>
                        {m.publicUrl}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
